/**
 * FilterWizardPeekPopover — eye-icon button rendered in the FilterWizard
 * header. When clicked, runs the current committed filters against the
 * underlying table (LIMIT 50) and shows the matching rows inline so the user
 * can sanity-check the filter before relying on it (e.g. before sampling for
 * JSON-structure inference, or before exporting filtered results).
 *
 * Generic — works anywhere FilterWizard is mounted, as long as the wizard
 * has connection + table context.
 */

import { useCallback, useState } from 'react';
import { Eye, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { fetchTableExplorerRows } from '../../../table-explorer/services/tableExplorerApi';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type {
  FilterGroup,
  TableExplorerColumn,
} from '../../../core/types/workspaceTypes';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';

const PEEK_LIMIT = 50;

export type FilterWizardPeekPopoverProps = {
  connectionId?: string;
  database?: string;
  tableSchema?: string;
  tableName?: string;
  engine?: DatabaseEngineId;
  filters: FilterGroup[];
};

type PeekState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'success';
      rows: Record<string, unknown>[];
      columns: TableExplorerColumn[];
      totalRowCount?: number;
      cappedAt: number;
    }
  | { status: 'error'; message: string };

const newExecutionId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? `filter-peek-${crypto.randomUUID()}`
    : `filter-peek-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export function FilterWizardPeekPopover({
  connectionId,
  database,
  tableSchema,
  tableName,
  engine,
  filters,
}: FilterWizardPeekPopoverProps) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<PeekState>({ status: 'idle' });
  const { track } = useAnalytics();

  // `tableSchema` may be `''` on schemaless engines (Kusto/ADX) — a present,
  // valid schema, not a missing one. Guard on `== null` so peek still runs;
  // truthy-checking would drop the whole popover for every Kusto filter.
  const hasContext = Boolean(connectionId && database && tableName) && tableSchema != null;

  const runPeek = useCallback(async () => {
    if (!connectionId || !database || tableSchema == null || !tableName) return;
    setState({ status: 'loading' });
    try {
      const result = await fetchTableExplorerRows({
        connectionId,
        executionId: newExecutionId(),
        table: { database, schema: tableSchema, tableName },
        columns: '*',
        limit: PEEK_LIMIT,
        filters,
        sorting: [],
      });
      if (!result.success) {
        setState({ status: 'error', message: result.error || 'Peek failed' });
        return;
      }
      setState({
        status: 'success',
        rows: result.rows ?? [],
        columns: result.columns ?? [],
        totalRowCount: result.totalRowCount,
        cappedAt: PEEK_LIMIT,
      });
      if (engine) {
        track('feature:filter_peek', {
          engine,
          filter_count: filters.length,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Peek failed';
      setState({ status: 'error', message });
    }
  }, [connectionId, database, tableSchema, tableName, engine, filters, track]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        void runPeek();
      } else {
        setState({ status: 'idle' });
      }
    },
    [runPeek],
  );

  if (!hasContext) return null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
          data-testid="filter-wizard-peek"
          title="Peek matching rows"
        >
          <Eye className="h-3 w-3 mr-1" />
          Peek
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(720px,calc(100vw-2rem))] p-0"
        data-testid="filter-wizard-peek-popover"
        portalled={false}
      >
        <PeekHeader
          state={state}
          onRefresh={runPeek}
          onClose={() => setOpen(false)}
        />
        <PeekBody state={state} />
      </PopoverContent>
    </Popover>
  );
}

function PeekHeader({
  state,
  onRefresh,
  onClose,
}: {
  state: PeekState;
  onRefresh: () => void;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b border-border">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {state.status === 'loading' && (
          <>
            <RandomDotmSpinner size={12} />
            <span>Running peek…</span>
          </>
        )}
        {state.status === 'success' && (
          <span className="font-medium">
            {state.rows.length}
            {state.rows.length === state.cappedAt
              ? ` of ${state.totalRowCount != null ? state.totalRowCount.toLocaleString() : '?'} rows (capped at ${state.cappedAt})`
              : ' rows'}
          </span>
        )}
        {state.status === 'error' && (
          <span className="text-destructive font-medium flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Error
          </span>
        )}
        {state.status === 'idle' && <span>Peek matching rows</span>}
      </div>
      <div className="flex items-center gap-1">
        {(state.status === 'success' || state.status === 'error') && (
          <button
            type="button"
            onClick={onRefresh}
            className="px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded"
            data-testid="filter-wizard-peek-refresh"
          >
            Refresh
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded"
          aria-label="Close peek"
          data-testid="filter-wizard-peek-close"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function PeekBody({ state }: { state: PeekState }) {
  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <div className="p-6 text-center text-xs text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="p-3 text-xs text-destructive break-words" data-testid="filter-wizard-peek-error">
        {state.message}
      </div>
    );
  }
  if (state.rows.length === 0) {
    return (
      <div className="p-6 text-center text-xs text-muted-foreground italic" data-testid="filter-wizard-peek-empty">
        No rows match the current filter.
      </div>
    );
  }
  // Render a compact table — first row's keys drive the column list so it
  // matches the response shape regardless of `columns` metadata casing.
  const firstRow = state.rows[0];
  const colNames =
    state.columns.length > 0
      ? state.columns.map((c) => c.name)
      : Object.keys(firstRow);
  return (
    <div
      className="overflow-auto"
      style={{ maxHeight: 320 }}
      data-testid="filter-wizard-peek-body"
    >
      <table className="w-full text-[11px]">
        <thead className="sticky top-0 bg-muted/80">
          <tr>
            {colNames.map((name) => (
              <th
                key={name}
                className="text-left px-2 py-1 font-medium text-muted-foreground border-b border-border whitespace-nowrap"
                translate="no"
              >
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {state.rows.map((row, i) => (
            <tr key={i} className="hover:bg-muted/40">
              {colNames.map((name) => {
                const v = row[name];
                const display =
                  v === null || v === undefined
                    ? ''
                    : typeof v === 'object'
                      ? JSON.stringify(v)
                      : String(v);
                return (
                  <td
                    key={name}
                    className="px-2 py-1 border-b border-border/40 whitespace-nowrap overflow-hidden text-ellipsis max-w-[240px]"
                    translate="no"
                    title={display}
                  >
                    {display === '' ? (
                      <span className="text-muted-foreground italic">NULL</span>
                    ) : (
                      display
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
